/**
 * The wait lobby — the screen that was missing.
 *
 * The server has always waited. `maybeBegin` wants every seat filled and every
 * crown claimed before it stands a realm up, and it has wanted that since the
 * room became several kingdoms rather than one. What the client did with that
 * wait was hide the menu and show nothing: a player pressed "take this crown",
 * the title screen vanished, and they sat in front of a black rectangle with no
 * way to tell whether they were waiting for a friend, for a server, or for
 * nothing at all. Two of the three look identical from there, and the third is a
 * bug.
 *
 * So this is a screen for the waiting. It says four things, and it is worth
 * naming them because each was unanswerable before:
 *
 *   - **Which room this is** — its name and the code somebody else has to type.
 *   - **Who is here**, seat by seat, and which kingdom each of them took.
 *   - **Who is not here**, which is the interesting one. A room reopened from a
 *     save keeps every crown for the person who wore it, so an empty chair is
 *     either *open* or *Ada's, and Ada is not up yet* — and a lobby that drew
 *     those the same way would have the next player take Ada's kingdom in
 *     perfectly good faith.
 *   - **What is being waited for**, in a sentence, at the top.
 *
 * Plus the host's two controls: begin now, with whoever is standing here, and
 * free a crown whose owner is not coming back. Both are refused server-side —
 * see the pause in `server/hearth.ts` — so what is drawn here is a courtesy to
 * the host rather than the rule itself.
 *
 * It paints from `session.view`, the server's own account of the room, and from
 * nothing else. There is no local model of who is in the room to drift out of
 * step with the one that counts.
 */
import { toHex } from '../data/palette.ts';
import { hydrateLeaderArt } from './artdrop.ts';
import { kingdom } from './kingdoms.ts';
import { leaderPortrait } from './leaderportrait.ts';
import type { AbsentCrown, Seating, Session } from '../net/session.ts';

/** Names come from strangers; they are printed, so they are escaped. */
function esc(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

const PORTRAIT = 38;

/**
 * One line of the seat list.
 *
 * `kind` is the whole point of the screen: four visibly different states, where
 * before there were two and one of them was a blank.
 */
interface Row {
  kind: 'seated' | 'choosing' | 'kept' | 'open' | 'gone';
  name: string;
  you: boolean;
  host: boolean;
  crown: { factionId: string; raceId: string; kingdomId: string; nation: number } | null;
}

/** The kingdom on a crown, drawn the way the kingdom screen draws it. */
function crownCell(c: Row['crown'], muted: boolean): string {
  if (!c) return `<div class="lb-face"></div><div class="lb-none">no kingdom yet</div>`;
  let k;
  try {
    k = kingdom(c.kingdomId);
  } catch {
    // A save from a build whose roster has since changed. The room still knows
    // the nation, so the seat is still real; only the face is unavailable.
    return `<div class="lb-face"></div><div class="lb-none">${esc(c.kingdomId)}</div>`;
  }
  return (
    `<div class="lb-face" style="border-color:${toHex(k.leader.look.accent)}">` +
    leaderPortrait(k.race.face, k.leader.look, { size: PORTRAIT, muted }) +
    `</div>` +
    `<div class="lb-crown"><b>${esc(k.leader.title)} ${esc(k.leader.name)}</b>` +
    `<span>${esc(k.race.name)}</span></div>`
  );
}

export class Lobby {
  private el: HTMLElement;
  private session: Session | null = null;
  private onLeave: (() => void) | null = null;
  /** The last markup written, so a roster that says nothing new writes nothing. */
  private last = '';

  constructor() {
    this.el = document.createElement('div');
    this.el.id = 'lobby';
    this.el.className = 'gone';
    (document.getElementById('ui-root') as HTMLElement).appendChild(this.el);
    this.injectStyle();
  }

  /** Take over the screen for this session, until the board arrives. */
  show(session: Session, onLeave: () => void): void {
    this.session = session;
    this.onLeave = onLeave;
    this.last = '';
    session.onLobby = () => this.paint();
    this.el.classList.remove('gone');
    this.paint();
  }

  hide(): void {
    if (this.session) this.session.onLobby = null;
    this.session = null;
    this.el.classList.add('gone');
    this.el.innerHTML = '';
    this.last = '';
  }

  get open(): boolean {
    return !this.el.classList.contains('gone');
  }

  /**
   * The seats, built once and read by everything below.
   *
   * Live seats first in seat order, then the crowns being kept, then whatever is
   * left over as open chairs. A seat that has gone *and* left no crown behind is
   * dropped rather than drawn: it is somebody who looked in and left, and a room
   * that listed them forever would be a room that looks permanently over-full.
   */
  private rows(): Row[] {
    const s = this.session;
    if (!s) return [];
    const view = s.view;
    const size = view?.size ?? s.players.length;
    const crownOf = (id: number): Seating | undefined => s.seating.find((c) => c.id === id);
    const out: Row[] = [];
    const seen = new Set<number>();
    for (const p of s.players) {
      const crown = crownOf(p.id);
      if (!p.alive) continue;
      if (crown) seen.add(crown.nation);
      out.push({
        kind: crown ? 'seated' : 'choosing',
        name: p.name,
        you: p.id === s.playerId,
        host: view ? view.host === p.id : false,
        crown: crown ?? null,
      });
    }
    for (const k of (view?.absent ?? []) as AbsentCrown[]) {
      if (seen.has(k.nation)) continue;
      seen.add(k.nation);
      out.push({ kind: 'kept', name: k.name, you: false, host: false, crown: k });
    }
    while (out.length < size) {
      out.push({ kind: 'open', name: '', you: false, host: false, crown: null });
    }
    return out;
  }

  /** The one sentence at the top: what, precisely, is being waited for. */
  private waitingFor(rows: Row[]): string {
    const s = this.session;
    if (!s) return '';
    if (s.refusal) return esc(s.refusal);
    const view = s.view;
    if (!view) return 'Reaching the room…';
    if (view.started) return 'The horns are going — raising your capital…';
    if (!view.ready) return 'Waiting for the host to say what realm this is.';
    const choosing = rows.filter((r) => r.kind === 'choosing').length;
    const kept = rows.filter((r) => r.kind === 'kept').length;
    const open = rows.filter((r) => r.kind === 'open').length;
    if (choosing) {
      const who = rows.find((r) => r.kind === 'choosing');
      return `Waiting for ${esc(who?.name ?? 'somebody')} to take a kingdom.`;
    }
    if (kept && !open) {
      const who = rows.filter((r) => r.kind === 'kept').map((r) => esc(r.name));
      return `Waiting for ${who.join(' and ')} to come back to ${
        who.length > 1 ? 'their crowns' : 'their crown'
      }.`;
    }
    if (open) {
      return `Waiting for ${open} more ${open > 1 ? 'players' : 'player'} — the room begins when every seat is taken.`;
    }
    return 'Every seat is taken. The room is about to begin.';
  }

  private paint(): void {
    const s = this.session;
    if (!s || !this.open) return;
    const view = s.view;
    const rows = this.rows();
    const host = s.holdsRoom;
    const hostName = rows.find((r) => r.host)?.name ?? '';
    const canBegin = host && !!view?.ready && (view?.crowned ?? 0) > 0 && !view?.started;

    const seats = rows
      .map((r, i) => {
        const muted = r.kind !== 'seated';
        const tag =
          r.kind === 'seated'
            ? r.you
              ? `<span class="lb-you">you</span>`
              : ''
            : r.kind === 'choosing'
              ? `<span class="lb-wait">choosing…</span>`
              : r.kind === 'kept'
                ? `<span class="lb-kept">kept for them</span>`
                : `<span class="lb-open">open</span>`;
        const who =
          r.kind === 'open'
            ? `<div class="lb-who"><b class="lb-dim">An empty chair</b><span>anybody may sit here</span></div>`
            : `<div class="lb-who"><b>${esc(r.name)}</b><span>${
                r.kind === 'kept' ? 'not in the room' : r.host ? 'holds the room' : 'seated'
              }</span></div>`;
        const free =
          r.kind === 'kept' && host && r.crown
            ? `<span class="lb-free" data-free="${r.crown.nation}">free it</span>`
            : '';
        return (
          `<div class="lb-seat ${r.kind}${r.you ? ' me' : ''}">` +
          `<div class="lb-n">${i + 1}</div>${who}${crownCell(r.crown, muted)}` +
          `<div class="lb-tag">${tag}${free}</div></div>`
        );
      })
      .join('');

    const html =
      `<div class="lb-wrap">` +
      `<div class="lb-head">` +
      `<div class="lb-title">${esc(view?.name ?? s.room)}</div>` +
      `<div class="lb-code">room code <b>${esc(view?.code ?? s.room)}</b></div>` +
      `</div>` +
      `<div class="lb-sub${s.refusal ? ' bad' : ''}">${this.waitingFor(rows)}</div>` +
      `<div class="lb-seats">${seats}</div>` +
      `<div class="lb-foot">` +
      (host
        ? `<span class="lb-go${canBegin ? '' : ' off'}" id="lb-begin">BEGIN THE RUN</span>` +
          `<span class="lb-note">Yours to start — the room will begin by itself once every seat is taken.</span>`
        : `<span class="lb-note">${
            hostName ? `${esc(hostName)} holds this room` : 'Waiting on the room'
          } — they begin it, they pause it, they file it.</span>`) +
      `<span class="lb-leave" id="lb-leave">LEAVE</span>` +
      `</div></div>`;

    if (html === this.last) return;
    this.last = html;
    this.el.innerHTML = html;
    const begin = this.el.querySelector('#lb-begin') as HTMLElement | null;
    if (begin) {
      begin.onclick = () => {
        if (!canBegin) return;
        s.beginNow();
      };
    }
    (this.el.querySelector('#lb-leave') as HTMLElement).onclick = () => {
      const leave = this.onLeave;
      this.hide();
      leave?.();
    };
    for (const el of this.el.querySelectorAll('[data-free]')) {
      (el as HTMLElement).onclick = () =>
        s.releaseCrown(Number((el as HTMLElement).dataset.free));
    }
    hydrateLeaderArt(this.el);
  }

  private injectStyle(): void {
    const st = document.createElement('style');
    st.textContent = `
      #lobby { position: absolute; inset: 0; z-index: 92; pointer-events: auto;
        background: radial-gradient(ellipse at 50% 38%, #24201b 0%, #0f0e0c 62%, #080807 100%);
        display: flex; align-items: flex-start; justify-content: center; overflow-y: auto;
        font-family: Inter, system-ui, sans-serif; color: #c9bfae; }
      #lobby.gone { display: none; }
      #lobby .lb-wrap { width: min(760px, 94vw); margin: auto; padding: 42px 0; }
      #lobby .lb-head { display: flex; align-items: baseline; justify-content: space-between;
        gap: 16px; border-bottom: 1px solid #2a2724; padding-bottom: 10px; }
      #lobby .lb-title { font-family: Cinzel, Georgia, serif; font-size: 27px; color: #d9cfbc;
        letter-spacing: .02em; }
      #lobby .lb-code { font: 11px ui-monospace, monospace; color: #6d675e;
        letter-spacing: .12em; text-transform: uppercase; }
      #lobby .lb-code b { color: #c8a44c; letter-spacing: .16em; }
      #lobby .lb-sub { margin: 12px 0 18px; font-size: 13.5px; color: #a89e8c; min-height: 19px; }
      #lobby .lb-sub.bad { color: #b8484b; }
      #lobby .lb-seats { display: flex; flex-direction: column; gap: 7px; }
      #lobby .lb-seat { display: grid; grid-template-columns: 22px 1fr auto 1fr auto;
        align-items: center; gap: 12px; padding: 9px 13px; border: 1px solid #24211d;
        background: rgba(22,20,17,0.72); border-radius: 3px; }
      #lobby .lb-seat.me { border-color: #4a3d1d; background: rgba(34,29,20,0.8); }
      #lobby .lb-seat.open { border-style: dashed; opacity: .62; }
      #lobby .lb-seat.kept { border-color: #3a3020; }
      #lobby .lb-n { font: 11px ui-monospace, monospace; color: #57524a; }
      #lobby .lb-who b { display: block; font-size: 14px; color: #d9cfbc; font-weight: 600; }
      #lobby .lb-who b.lb-dim { color: #6d675e; font-style: italic; font-weight: 400; }
      #lobby .lb-who span { font-size: 10.5px; color: #6d675e; letter-spacing: .06em;
        text-transform: uppercase; }
      #lobby .lb-face { width: ${PORTRAIT}px; height: ${PORTRAIT}px; border-radius: 2px;
        border: 1px solid #2a2724; overflow: hidden; }
      #lobby .lb-face > * { display: block; }
      #lobby .lb-crown b { display: block; font-size: 13px; color: #c9bfae; font-weight: 600; }
      #lobby .lb-crown span { font-size: 10.5px; color: #6d675e; }
      #lobby .lb-none { font-size: 11.5px; color: #57524a; font-style: italic; }
      #lobby .lb-tag { display: flex; align-items: center; gap: 8px; justify-self: end; }
      #lobby .lb-you { font: 10px ui-monospace, monospace; color: #c8a44c;
        border: 1px solid #4a3d1d; padding: 2px 6px; border-radius: 2px; letter-spacing: .1em; }
      #lobby .lb-wait, #lobby .lb-kept, #lobby .lb-open { font: 10px ui-monospace, monospace;
        color: #6d675e; letter-spacing: .1em; text-transform: uppercase; }
      #lobby .lb-kept { color: #8a6c26; }
      #lobby .lb-free { font: 10px ui-monospace, monospace; color: #9c3336; cursor: pointer;
        border: 1px solid #3a2020; padding: 2px 6px; border-radius: 2px; }
      #lobby .lb-free:hover { color: #d9cfbc; background: #3a2020; }
      #lobby .lb-foot { display: flex; align-items: center; gap: 14px; margin-top: 22px;
        flex-wrap: wrap; }
      #lobby .lb-go { font-family: Cinzel, Georgia, serif; font-size: 14px; letter-spacing: .14em;
        color: #e6d9b8; background: #33291a; border: 1px solid #6b5726; padding: 9px 20px;
        border-radius: 2px; cursor: pointer; }
      #lobby .lb-go:hover { background: #4a3a20; }
      #lobby .lb-go.off { opacity: .38; cursor: default; }
      #lobby .lb-leave { margin-left: auto; font: 11px ui-monospace, monospace; color: #6d675e;
        cursor: pointer; letter-spacing: .12em; border: 1px solid #2a2724; padding: 8px 14px;
        border-radius: 2px; }
      #lobby .lb-leave:hover { color: #c9bfae; border-color: #4a453d; }
      #lobby .lb-note { font-size: 11.5px; color: #6d675e; max-width: 380px; }
    `;
    document.head.appendChild(st);
  }
}
